import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Home,
  ChevronRight,
  ChevronLeft,
  Expand,
  Ship,
  ShieldCheck,
  X,
  Ruler,
  Clock,
  Package,
  Flag,
  Calendar,
  Gem,
} from 'lucide-react';
import { SuggestedCarousell } from '../suggested-carousell';
import './details.css';
import OptimizedImage from '@/utils/OptimizedImage'; // Make sure to adjust the import path
import { CartAddedModal } from './CartAddedModal';

type Listing = {
  _id: string;
  name: string;
  brand: string;
  model: string;
  price: number;
  thumbnailImages: string[];
  image: string;
  vintage: boolean;
  preOwned: boolean;
  isNewWatch: boolean;
  box: boolean;
  papers: boolean;
  caseSize: number;
  lugWidth: number;
  lugToLugLength: number;
  caseThickness: number;
  description?: string;
  curatorDescription?: string;
  slug: string;
  sold: boolean;
  images?: string[];
  movement?: string;
  caseMaterial?: string;
  bandMaterial?: string;
  countryOfManufacture?: string;
  yearManufactured?: string;
  waterResistance?: string;
  numberOfJewels?: string;
};

export function WatchDetails({ watch }: { watch: Listing }) {
  const [selectedImage, setSelectedImage] = useState(watch.image);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNav, setShowNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showCartModal, setShowCartModal] = useState(false);
  const { addToCart } = useCart();

  const allImages = [
    watch.image,
    ...watch.thumbnailImages.filter((img) => img !== watch.image),
  ];

  useEffect(() => {
    setSelectedImage(watch.image);
  }, [watch]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY) {
        setShowNav(true);
      } else {
        setShowNav(currentScrollY > 50);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  useEffect(() => {
    document.body.style.overflow = isFullscreen ? 'hidden' : 'auto';
  }, [isFullscreen]);

  const handleAddToCart = () => {
    addToCart({
      ...watch,
      quantity: 1,
    });
    setShowCartModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Fixed Navigation */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50 px-4 py-3"
        initial={{ opacity: 1, y: 0 }}
        animate={{
          opacity: showNav ? 1 : 0,
          y: showNav ? 0 : -100,
          transition: { duration: 0.3, ease: 'easeInOut' },
        }}
      >
        <div className="container mx-auto flex items-center space-x-2 text-slate-400">
          <Link
            href="/marketplace"
            className="hover:text-blue-400 transition-colors duration-300"
          >
            <Home className="h-4 w-4" />
          </Link>
          <ChevronRight className="h-4 w-4" />
          <Link
            href="/marketplace"
            className="hover:text-blue-400 transition-colors duration-300"
          >
            Marketplace
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-blue-400">{watch.brand}</span>
        </div>
      </motion.nav>

      {/* Main Content */}
      <div className="container mx-auto px-4">
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <Card className="relative overflow-hidden bg-slate-800/50 backdrop-blur-sm border-slate-700/50 shadow-xl rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 group">
              <div className="relative aspect-square">
                <OptimizedImage
                  src={selectedImage}
                  alt={watch.name}
                  width={800}
                  height={800}
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                  priority={true}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-4 right-4 space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => setIsFullscreen(true)}
                    className="backdrop-blur-sm bg-black/50 hover:bg-black/70 transition-colors duration-300"
                  >
                    <Expand className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-sm bg-black/50 hover:bg-black/70"
                  onClick={() => {
                    const currentIndex = allImages.indexOf(selectedImage);
                    const prevIndex =
                      (currentIndex - 1 + allImages.length) % allImages.length;
                    setSelectedImage(allImages[prevIndex]);
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-sm bg-black/50 hover:bg-black/70"
                  onClick={() => {
                    const currentIndex = allImages.indexOf(selectedImage);
                    const nextIndex = (currentIndex + 1) % allImages.length;
                    setSelectedImage(allImages[nextIndex]);
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </Card>

            {/* Thumbnail Gallery */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 shadow-xl rounded-xl p-4 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
              <div className="flex gap-4 flex-wrap">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(image)}
                    className={`relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden transition-all duration-300 ${
                      selectedImage === image
                        ? 'ring-2 ring-blue-500 scale-95 shadow-lg shadow-blue-500/20'
                        : 'hover:ring-2 hover:ring-blue-500/50 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10'
                    }`}
                  >
                    <OptimizedImage
                      src={image}
                      alt={`${watch.name} thumbnail ${index + 1}`}
                      width={80}
                      height={80}
                      className="object-cover"
                      sizes="80px"
                    />
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Right Column - Watch Details */}
          <div className="lg:sticky lg:top-24 space-y-6 h-fit">
            {/* Title and Price Card */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50 shadow-xl rounded-xl p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
              <div className="space-y-6">
                {/* Title and Badges */}
                <div>
                  <h1 className="text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
                    {watch.name}
                  </h1>
                  <div className="flex flex-wrap gap-2">
                    {watch.vintage && (
                      <Badge
                        variant="secondary"
                        className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:bg-slate-700/50 transition-colors duration-300"
                      >
                        Vintage
                      </Badge>
                    )}
                    {watch.preOwned && (
                      <Badge
                        variant="secondary"
                        className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:bg-slate-700/50 transition-colors duration-300"
                      >
                        Pre-Owned
                      </Badge>
                    )}
                    {watch.isNewWatch && (
                      <Badge
                        variant="secondary"
                        className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:bg-slate-700/50 transition-colors duration-300"
                      >
                        New
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Price and Add to Cart */}
                <div className="space-y-4">
                  <div className="flex items-baseline justify-between">
                    <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                      ${watch.price.toLocaleString()}
                    </span>
                    <span className="text-blue-400">Free shipping</span>
                  </div>

                  <Button
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white shadow-lg rounded-xl transition-all duration-300 hover:shadow-blue-500/25 hover:scale-[1.02]"
                    size="lg"
                    onClick={handleAddToCart}
                    disabled={watch.sold}
                  >
                    {watch.sold ? 'Sold Out' : 'Add to Cart'}
                  </Button>

                  <CartAddedModal
                    isOpen={showCartModal}
                    onClose={() => setShowCartModal(false)}
                    watch={watch}
                  />

                  <div className="flex flex-col space-y-2 pt-2">
                    <div className="flex items-center text-slate-400 space-x-2 group">
                      <Ship className="h-4 w-4 group-hover:text-blue-400 transition-colors duration-300" />
                      <span className="text-sm group-hover:text-blue-400 transition-colors duration-300">
                        International shipping available
                      </span>
                    </div>
                    <div className="flex items-center text-slate-400 space-x-2 group">
                      <ShieldCheck className="h-4 w-4 group-hover:text-blue-400 transition-colors duration-300" />
                      <span className="text-sm group-hover:text-blue-400 transition-colors duration-300">
                        Authenticity guaranteed
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Specifications Card */}
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50 shadow-xl rounded-xl p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
              <h2 className="text-xl font-semibold text-slate-200 mb-4">
                Specifications
              </h2>
              <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                {[
                  {
                    label: 'Case Size',
                    value: `${watch.caseSize}mm`,
                    icon: Ruler,
                  },
                  { label: 'Movement', value: watch.movement, icon: Clock },
                  {
                    label: 'Case Material',
                    value: watch.caseMaterial,
                    icon: Package,
                  },
                  {
                    label: 'Band Material',
                    value: watch.bandMaterial,
                    icon: Package,
                  },
                  {
                    label: 'Country',
                    value: watch.countryOfManufacture,
                    icon: Flag,
                  },
                  {
                    label: 'Year',
                    value: watch.yearManufactured,
                    icon: Calendar,
                  },
                  {
                    label: 'Water Resistance',
                    value: watch.waterResistance,
                    icon: Package,
                  },
                  { label: 'Jewels', value: watch.numberOfJewels, icon: Gem },
                ].map(
                  ({ label, value, icon: Icon }) =>
                    value && (
                      <div
                        key={label}
                        className="flex items-start space-x-2 group"
                      >
                        <Icon className="h-4 w-4 text-slate-400 mt-1 group-hover:text-blue-400 transition-colors duration-300" />
                        <div>
                          <dt className="text-sm text-slate-400 group-hover:text-blue-400 transition-colors duration-300">
                            {label}
                          </dt>
                          <dd className="text-slate-200">{value}</dd>
                        </div>
                      </div>
                    )
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Description Card */}
        <div className="mt-8">
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700/50 shadow-xl rounded-xl p-6 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <h2 className="text-xl font-semibold text-slate-200 mb-4">
              Description
            </h2>
            <p className="text-slate-400 whitespace-pre-wrap leading-relaxed">
              {watch.description || watch.curatorDescription}
            </p>
          </Card>
        </div>

        {/* Enhanced Fullscreen Image Modal */}
        <AnimatePresence>
          {isFullscreen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center"
            >
              <div className="relative w-full h-full flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 text-white hover:bg-white/20 transition-colors duration-300"
                  onClick={() => setIsFullscreen(false)}
                >
                  <X className="h-6 w-6" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 transition-colors duration-300"
                  onClick={() => {
                    const currentIndex = allImages.indexOf(selectedImage);
                    const prevIndex =
                      (currentIndex - 1 + allImages.length) % allImages.length;
                    setSelectedImage(allImages[prevIndex]);
                  }}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <OptimizedImage
                  src={selectedImage}
                  alt={watch.name}
                  width={1200}
                  height={1200}
                  className="max-w-[90vw] max-h-[90vh] object-contain"
                  priority={true}
                  sizes="90vw"
                />

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 transition-colors duration-300"
                  onClick={() => {
                    const currentIndex = allImages.indexOf(selectedImage);
                    const nextIndex = (currentIndex + 1) % allImages.length;
                    setSelectedImage(allImages[nextIndex]);
                  }}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Suggested Watches */}
        <div className="mt-16">
          <SuggestedCarousell currentListingId={watch._id} />
        </div>
      </div>
    </div>
  );
}

export default WatchDetails;
